<template>
  <div class="affiliate-dashboard" :class="{ 'affiliate-dashboard--dark': isDarkSkin }">
    <breadcrumb-button>
      <template #items>
        <b-dropdown-item v-if="hasPermission(permissions.EXPORT_EXCEL)" @click="exportExcel">
          <i class="fa-regular fa-file-excel mr-50"></i>
          <span class="align-middle">{{ $t('operations.export') }}</span>
        </b-dropdown-item>
      </template>
    </breadcrumb-button>

    <div class="affiliate-dashboard__toolbar">
      <filter-card :filter-key="viewKey" dashboard v-model="filter" v-bind:fields="filterFields"/>
      <calendar-bar v-model="dateRange" @input="refresh"/>
    </div>

    <b-row class="kpi-row">
      <b-col md="6" sm="12" class="kpi-col mb-2">
        <div class="kpi-card kpi-card--primary">
          <div class="kpi-card__icon">
            <feather-icon icon="UsersIcon" size="24"/>
          </div>
          <div class="kpi-card__body">
            <div class="kpi-card__title">{{ $t('affiliateDashboard.leadsFtds') }}</div>
            <!-- FTD side = monetaryFtdCount: the REAL transaction-based count
                 (family counted once, retention-BU + refunded/demoted excluded)
                 — same rule as the sales/mobile dashboards. totals.ftds is the
                 client-ftdTime figure that stays in the table's FTDs column. -->
            <div class="kpi-card__value">{{ totals.leads || 0 }} <span class="kpi-card__value-sep">/</span> {{ totals.monetaryFtdCount || 0 }}</div>
            <div class="kpi-card__footer">
              <feather-icon icon="TrendingUpIcon" size="12" class="mr-25"/>
              {{ $t('affiliateDashboard.conversion') }}: {{ formatPercent(totals.conversionRate) }}
              ({{ totals.conversions || 0 }} {{ $t('affiliateDashboard.converted').toLowerCase() }})
              <i class="fa-regular fa-circle-question ad2-info"
                 v-b-tooltip.hover.bottom :title="$t('affiliateDashboard.info.converted')"></i>
            </div>
          </div>
          <div class="kpi-card__glow"></div>
        </div>
      </b-col>

      <b-col md="6" sm="12" class="kpi-col mb-2">
        <div class="kpi-card kpi-card--success">
          <div class="kpi-card__icon">
            <i class="fa-solid fa-coins" style="font-size: 22px;"></i>
          </div>
          <div class="kpi-card__body">
            <div class="kpi-card__title">{{ $t('columns.netDeposits') }}</div>
            <div class="kpi-card__value">{{ convertToEURO(totals.netDeposits || 0) }}</div>
            <div class="kpi-card__footer">
              <feather-icon icon="ArrowDownCircleIcon" size="12" class="mr-25"/>
              {{ $t('affiliateDashboard.withdrawals') }}: {{ convertToEURO(totals.withdrawals || 0) }}
              · {{ $t('affiliateDashboard.netTotal') }}: {{ convertToEURO(totals.netTotal || 0) }}
            </div>
          </div>
          <div class="kpi-card__glow"></div>
        </div>
      </b-col>

      <b-col md="6" sm="12" class="kpi-col mb-2">
        <div class="kpi-card kpi-card--warning">
          <div class="kpi-card__icon">
            <feather-icon icon="StarIcon" size="24"/>
          </div>
          <div class="kpi-card__body">
            <div class="kpi-card__title">{{ $t('columns.ftdAmount') }}</div>
            <div class="kpi-card__value">{{ convertToEURO(totals.ftdAmount || 0) }}</div>
            <div class="kpi-card__footer">
              <feather-icon icon="DivideCircleIcon" size="12" class="mr-25"/>
              {{ $t('affiliateDashboard.avgFtd') }}: {{ totals.avgFtdAmount != null ? convertToEURO(totals.avgFtdAmount) : '—' }}
            </div>
          </div>
          <div class="kpi-card__glow"></div>
        </div>
      </b-col>

      <b-col md="6" sm="12" class="kpi-col mb-2">
        <div class="kpi-card kpi-card--danger">
          <div class="kpi-card__icon">
            <feather-icon icon="CopyIcon" size="24"/>
          </div>
          <div class="kpi-card__body">
            <div class="kpi-card__title">{{ $t('affiliateDashboard.duplicated') }}</div>
            <!-- Shows the duplicated clients who made an FTD (ftdTime in range).
                 The plain duplicated-clients count is hidden — restore by
                 prepending: {{ duplicated.leads || 0 }} <span class="kpi-card__value-sep">/</span> -->
            <div class="kpi-card__value">{{ duplicated.ftds || 0 }}</div>
            <div class="kpi-card__footer">
              <feather-icon icon="StarIcon" size="12" class="mr-25"/>
              {{ $t('columns.ftdAmount') }}: {{ convertToEURO(duplicated.ftdAmount || 0) }}
            </div>
          </div>
          <div class="kpi-card__glow"></div>
        </div>
      </b-col>

      <b-col md="6" sm="12" class="kpi-col mb-2">
        <div class="kpi-card kpi-card--pending">
          <div class="kpi-card__icon">
            <feather-icon icon="ClockIcon" size="24"/>
          </div>
          <div class="kpi-card__body">
            <div class="kpi-card__title">{{ $t('affiliateDashboard.pendingApprovals') }}</div>
            <div class="kpi-card__value">{{ dashboard.pendingDepositsCount || 0 }} <span class="kpi-card__value-sep">/</span> {{ dashboard.pendingWithdrawalsCount || 0 }}</div>
            <div class="kpi-card__footer">
              <feather-icon icon="ClockIcon" size="12" class="mr-25"/>
              {{ $t('monetaryDashboard.pendingDeposits') }} · {{ $t('monetaryDashboard.pendingWithdrawals') }}
            </div>
          </div>
          <div class="kpi-card__glow"></div>
        </div>
      </b-col>
    </b-row>

    <div class="panel-card">
      <div class="panel-card__header">
        <div class="panel-card__title-wrap">
          <div class="panel-card__icon-chip">
            <feather-icon icon="TrendingUpIcon" size="18"/>
          </div>
          <h5 class="panel-card__title mb-0">{{ $t('affiliateDashboard.title') }}</h5>
          <b-badge variant="light-primary">{{ rows.length }} {{ $t('affiliateDashboard.rows') }}</b-badge>
        </div>

        <div class="ad2-header-actions">
          <div class="ad2-groupby">
            <b-button v-for="opt in groupOptions" :key="opt.value" size="sm"
                      :variant="groupBy === opt.value ? 'primary' : 'outline-primary'"
                      @click="setGroupBy(opt.value)">
              {{ opt.label }}
            </b-button>
          </div>
          <b-button size="sm" variant="flat-success" v-if="hasPermission(permissions.EXPORT_EXCEL)"
                    :disabled="exporting" @click="exportExcel">
            <b-spinner v-if="exporting" small class="mr-50"/>
            <i v-else class="fa-regular fa-file-excel mr-50"></i>{{ $t('operations.export') }}
          </b-button>
        </div>
      </div>

      <div class="panel-card__body">
        <b-table
            responsive
            v-handy-scroll
            :items="rows"
            :fields="fields"
            :busy="loading"
            :tbody-tr-class="rowClass"
            striped
            show-empty
            no-footer-sorting
            foot-clone
            style="white-space: nowrap; padding-bottom: 30px"
            :sort-compare="sortItemCompare"
        >
          <template #table-busy>
            <div class="text-center my-3">
              <b-spinner class="align-middle mr-1"/>
              <strong>{{ $t('affiliateDashboard.loading') }}</strong>
            </div>
          </template>

          <template #empty>
            <div class="text-center text-muted my-2">{{ $t('globalMessages.noDataFound') }}</div>
          </template>

          <!-- Label -->
          <template #cell(label)="data">
            <b-badge v-if="data.item.duplicated" variant="light-warning" class="font-weight-bold">
              <feather-icon icon="CopyIcon" size="12" class="mr-25"/>{{ $t('affiliateDashboard.duplicated') }}
            </b-badge>
            <b-link v-else-if="groupBy === 'AFFILIATE' && data.item.affiliateId"
                    @click="onAffiliateSelected(data.item.affiliateId)">
              {{ data.value }}
            </b-link>
            <span v-else>{{ data.value }}</span>
          </template>

          <!-- Desk -->
          <template #cell(desk)="data">
            <span v-if="data.value">{{ data.value }}</span>
            <span v-else class="text-muted">—</span>
          </template>

          <!-- Header info: FTDs vs Converted (deposit date vs lead date) -->
          <template #head(ftds)>
            {{ $t('columns.ftds') }}
            <i class="fa-regular fa-circle-question ad2-info"
               v-b-tooltip.hover.top :title="$t('affiliateDashboard.info.ftds')"></i>
          </template>
          <template #head(conversions)>
            {{ $t('affiliateDashboard.converted') }}
            <i class="fa-regular fa-circle-question ad2-info"
               v-b-tooltip.hover.top :title="$t('affiliateDashboard.info.converted')"></i>
          </template>
          <template #head(conversionRate)>
            {{ $t('columns.conversionRate') }}
            <i class="fa-regular fa-circle-question ad2-info"
               v-b-tooltip.hover.top :title="$t('affiliateDashboard.info.conversionRate')"></i>
          </template>

          <!-- FTDs -->
          <template #cell(ftds)="data">
            <span class="font-weight-bold">{{ data.value }}</span>
          </template>

          <!-- Conversion Rate -->
          <template #cell(conversionRate)="data">
            <b-badge :variant="conversionVariant(data.value)">{{ formatPercent(data.value) }}</b-badge>
          </template>

          <!-- CPA / CRG contract -->
          <template #cell(cpa)="data">
            <span v-if="data.value != null">{{ convertToEURO(data.value) }}</span>
            <span v-else class="text-muted">—</span>
          </template>
          <template #cell(crg)="data">
            <span v-if="data.value != null">{{ data.value }}%</span>
            <span v-else class="text-muted">—</span>
          </template>
          <!-- Guaranteed FTDs: the number we pay for AT LEAST. One clean number;
               the tooltip explains the formula, the click opens the full math. -->
          <template #cell(expectedFtds)="data">
            <button v-if="data.value != null" type="button" class="ad-crg-link"
                    v-b-tooltip.hover :title="crgMinTooltip(data.item)"
                    @click.stop="openCrgDetails(data.item)">
              <span class="ad-crg-min">{{ data.value }}</span>
            </button>
            <span v-else class="text-muted">—</span>
          </template>
          <template #cell(cpaCost)="data">
            <span v-if="data.value != null">{{ convertToEURO(data.value) }}</span>
            <span v-else class="text-muted">—</span>
          </template>
          <!-- CRG standing: FTDs vs guaranteed minimum + a plain-words state.
               One clean line — the tooltip and the modal carry the details. -->
          <template #cell(crgStatus)="data">
            <button v-if="data.item.crgGuaranteedFtds != null" type="button" class="ad-crg-link text-nowrap"
                    v-b-tooltip.hover.left :title="crgStatusTooltip(data.item)"
                    @click.stop="openCrgDetails(data.item)">
              <span class="ad-crg-count">
                {{ data.item.crgInWindowFtds || 0 }}<span class="ad-crg-count__sep">/</span>{{ data.item.crgGuaranteedFtds }}
              </span>
              <span class="ad-crg-state" :class="`ad-crg-state--${crgState(data.item).tone}`">
                {{ crgState(data.item).label }}
              </span>
            </button>
            <span v-else class="text-muted">—</span>
          </template>

          <!-- Money -->
          <template #cell(ftdAmount)="data">
            <span :style="{ color: data.value > 0 ? '#4ebd52' : data.value < 0 ? '#c53838' : undefined }">
              {{ convertToEURO(data.value) }}
            </span>
          </template>
          <template #cell(avgFtdAmount)="data">
            <span v-if="data.value != null">{{ convertToEURO(data.value) }}</span>
            <span v-else class="text-muted">—</span>
          </template>
          <template #cell(netDeposits)="data">
            <span :style="{ color: data.value > 0 ? '#4ebd52' : data.value < 0 ? '#c53838' : undefined }">
              {{ convertToEURO(data.value) }}
            </span>
          </template>
          <template #cell(withdrawals)="data">
            <span :style="{ color: data.value < 0 ? '#c53838' : undefined }">{{ convertToEURO(data.value) }}</span>
          </template>
          <template #cell(netTotal)="data">
            <span class="font-weight-bold"
                  :style="{ color: data.value > 0 ? '#4ebd52' : data.value < 0 ? '#c53838' : undefined }">
              {{ convertToEURO(data.value) }}
            </span>
          </template>

          <!-- Countries -->
          <template #cell(countries)="data">
            <span v-if="hasEntries(data.value)" v-b-tooltip.hover.left :title="countriesTooltip(data.value)">
              <span v-for="entry in topEntries(data.value, 3)" :key="entry.key" class="ad2-chip">
                {{ countryNames[entry.key] || entry.key }} <b>{{ entry.count }}</b>
              </span>
              <b-badge v-if="Object.keys(data.value).length > 3" variant="light-secondary">
                +{{ Object.keys(data.value).length - 3 }}
              </b-badge>
            </span>
            <span v-else class="text-muted">—</span>
          </template>

          <!-- Statuses -->
          <template #cell(statuses)="data">
            <span v-if="hasEntries(data.value)" v-b-tooltip.hover.left :title="statusesTooltip(data.value)">
              <span v-for="entry in topEntries(data.value, 3)" :key="entry.key" class="ad2-chip">
                <span class="ad2-chip__dot" :style="{ backgroundColor: statusColor(entry.key) }"></span>
                {{ statusName(entry.key) }} <b>{{ entry.count }}</b>
              </span>
              <b-badge v-if="Object.keys(data.value).length > 3" variant="light-secondary">
                +{{ Object.keys(data.value).length - 3 }}
              </b-badge>
            </span>
            <span v-else class="text-muted">—</span>
          </template>

          <!-- FOOTER -->
          <template #foot(label)>
            <span class="ad2-foot">{{ $t('columns.total') }}</span>
          </template>
          <template #foot(orgName)><span/></template>
          <template #foot(desk)><span/></template>
          <template #foot(leads)>
            <span class="ad2-foot">{{ totals.leads || 0 }}</span>
          </template>
          <template #foot(ftds)>
            <span class="ad2-foot">{{ totals.ftds || 0 }}</span>
          </template>
          <template #foot(conversions)>
            <span class="ad2-foot">{{ totals.conversions || 0 }}</span>
          </template>
          <template #foot(conversionRate)>
            <span class="ad2-foot">{{ formatPercent(totals.conversionRate) }}</span>
          </template>
          <template #foot(cpa)><span/></template>
          <template #foot(crg)><span/></template>
          <template #foot(expectedFtds)>
            <span class="ad2-foot">{{ totals.expectedFtds != null ? totals.expectedFtds : '' }}</span>
          </template>
          <template #foot(cpaCost)>
            <span class="ad2-foot">{{ totals.cpaCost != null ? convertToEURO(totals.cpaCost) : '' }}</span>
          </template>
          <template #foot(ftdAmount)>
            <b-badge variant="success" class="ad2-foot">{{ convertToEURO(totals.ftdAmount || 0) }}</b-badge>
          </template>
          <template #foot(avgFtdAmount)>
            <span class="ad2-foot">{{ totals.avgFtdAmount != null ? convertToEURO(totals.avgFtdAmount) : '' }}</span>
          </template>
          <template #foot(netDeposits)>
            <b-badge :variant="(totals.netDeposits || 0) > 0 ? 'success' : 'danger'" class="ad2-foot">
              {{ convertToEURO(totals.netDeposits || 0) }}
            </b-badge>
          </template>
          <template #foot(withdrawals)>
            <b-badge variant="danger" class="ad2-foot">{{ convertToEURO(totals.withdrawals || 0) }}</b-badge>
          </template>
          <template #foot(netTotal)>
            <b-badge :variant="(totals.netTotal || 0) > 0 ? 'success' : 'danger'" class="ad2-foot">
              {{ convertToEURO(totals.netTotal || 0) }}
            </b-badge>
          </template>
          <template #foot(countries)>
            <span class="ad2-foot" v-b-tooltip.hover.left :title="countriesTooltip(totals.countries)">
              {{ Object.keys(totals.countries || {}).length }} {{ $t('columns.countries').toLowerCase() }}
            </span>
          </template>
          <template #foot(statuses)>
            <span class="ad2-foot" v-b-tooltip.hover.left :title="statusesTooltip(totals.statuses)">
              {{ Object.keys(totals.statuses || {}).length }} {{ $t('affiliateDashboard.statuses').toLowerCase() }}
            </span>
          </template>
        </b-table>
      </div>
    </div>

    <affiliate-modal ref="affiliate-modal"/>
    <affiliate-crg-modal ref="crgModal"/>
  </div>
</template>

<script>
import CalendarBar from '@/components/widget/CalendarBar';
import mixinBase from '@/components/mixin/mixinBase';
import { mapActions, mapGetters } from 'vuex';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import FilterCard from '@/components/widget/FilterCard';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import AffiliateDashboardFilterFields from '@/model/Dashboard/AffiliateDashboardFilterFields';
import BreadcrumbButton from '@/components/widget/breadcrumbButton.vue';
import AffiliateModal from '@/components/modal/affiliateModal.vue';
import AffiliateCrgModal from '@/components/modal/AffiliateCrgModal.vue';

export default {
  name: 'AffiliateDashboard',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],

  components: {
    AffiliateModal,
    AffiliateCrgModal,
    BreadcrumbButton,
    CalendarBar,
    FilterCard,
  },

  data: () => ({
    filter: {
      range: { from: null, until: null },
      affiliateIds: [],
      orgIds: [],
      buIds: null,
      desks: [],
    },
    filterFields: [],
    groupBy: 'AFFILIATE',
    dashboard: {},
    dateRange: { from: '', until: '' },
    loading: false,
    exporting: false,
  }),

  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    ...mapGetters('clients', ['allStatuses']),

    viewKey() {
      return this.$route.name || 'affiliate-dashboard';
    },
    rows() {
      return this.dashboard.rows || [];
    },
    totals() {
      return this.dashboard.totals || {};
    },
    duplicated() {
      return this.dashboard.duplicated || {};
    },
    groupOptions() {
      return [
        { value: 'AFFILIATE', label: this.$t('affiliateDashboard.byAffiliate') },
        { value: 'DESK', label: this.$t('affiliateDashboard.byDesk') },
        { value: 'ORGANIZATION', label: this.$t('affiliateDashboard.byOrganization') },
        { value: 'BUSINESS_UNIT', label: this.$t('affiliateDashboard.byBusinessUnit') },
      ];
    },
    groupLabel() {
      const option = this.groupOptions.find(o => o.value === this.groupBy);
      return option ? option.label : this.groupBy;
    },
    countryNames() {
      const countryList = this.$t('countryList');
      if (!Array.isArray(countryList)) return {};
      return countryList.reduce((acc, c) => {
        acc[c.code] = c.name;
        return acc;
      }, {});
    },
    statusById() {
      return (this.allStatuses || []).reduce((map, status) => {
        map[status.id] = { name: status.status, color: status.color };
        return map;
      }, {});
    },
    fields() {
      const fields = [
        { key: 'label', label: this.groupLabel, sortable: true },
      ];
      if (this.groupBy === 'BUSINESS_UNIT') {
        fields.push({ key: 'orgName', label: this.$t('affiliateDashboard.organization'), sortable: true });
      }
      if (this.groupBy !== 'DESK') {
        fields.push({ key: 'desk', label: this.$t('affiliateDashboard.desk'), sortable: true });
      }
      fields.push(
          { key: 'leads', label: this.$t('columns.leads'), sortable: true },
          { key: 'ftds', label: this.$t('columns.ftds'), sortable: true },
          { key: 'conversions', label: this.$t('affiliateDashboard.converted'), sortable: true },
          { key: 'conversionRate', label: this.$t('columns.conversionRate'), sortable: true },
      );
      if (this.groupBy === 'AFFILIATE') {
        fields.push(
            { key: 'cpa', label: this.$t('affiliateDashboard.cpa'), sortable: true },
            { key: 'crg', label: this.$t('affiliateDashboard.crg'), sortable: true },
            { key: 'expectedFtds', label: this.$t('affiliateDashboard.expectedFtds'), sortable: true },
            { key: 'crgStatus', label: this.$t('affiliateDashboard.crgStatus'), sortable: false },
            { key: 'cpaCost', label: this.$t('affiliateDashboard.cpaCost'), sortable: true },
        );
      }
      fields.push(
          { key: 'ftdAmount', label: this.$t('columns.ftdAmount'), sortable: true },
          { key: 'avgFtdAmount', label: this.$t('affiliateDashboard.avgFtd'), sortable: true },
          { key: 'netDeposits', label: this.$t('columns.netDeposits'), sortable: true },
          { key: 'withdrawals', label: this.$t('affiliateDashboard.withdrawals'), sortable: true },
          { key: 'netTotal', label: this.$t('affiliateDashboard.netTotal'), sortable: true },
          { key: 'countries', label: this.$t('columns.countries'), sortable: true },
          { key: 'statuses', label: this.$t('affiliateDashboard.statuses'), sortable: false },
      );
      return fields;
    },
  },

  watch: {
    filter: {
      deep: true,
      handler() {
        this.fetchData();
      },
    },
  },

  created() {
    this.filterFields = AffiliateDashboardFilterFields();
    this.filter.range = {
      from: this.$moment().startOf('week').format('YYYY-MM-DD'),
      until: this.$moment().endOf('week').format('YYYY-MM-DD'),
    };
    this.refreshData();
  },

  methods: {
    ...mapActions('dashboard', ['getAffiliateDashboard', 'downloadAffiliateDashboard']),
    ...mapActions('clients', ['refreshData']),

    refresh() {
      this.filter.range = {
        from: this.dateRange.from,
        until: this.dateRange.until,
      };
    },
    setGroupBy(groupBy) {
      if (this.groupBy === groupBy) return;
      this.groupBy = groupBy;
      this.fetchData();
    },
    async fetchData() {
      this.loading = true;
      try {
        this.dashboard = await this.getAffiliateDashboard({ ...this.filter, groupBy: this.groupBy }) || {};
      } catch (err) {
        this.showErrorNotification(
            this.$t('globalMessages.fetchDataError', { system: this.$t('menu.affiliateDashboard') }),
            this.getErrorDescription(err),
        );
      } finally {
        this.loading = false;
      }
    },
    async exportExcel() {
      this.exporting = true;
      try {
        await this.downloadAffiliateDashboard({ ...this.filter, groupBy: this.groupBy });
      } catch (err) {
        this.showErrorNotification(
            this.$t('globalMessages.fetchDataError', { system: this.$t('menu.affiliateDashboard') }),
            this.getErrorDescription(err),
        );
      } finally {
        this.exporting = false;
      }
    },
    onAffiliateSelected(affiliateId) {
      this.$refs['affiliate-modal'].showModal(affiliateId);
    },
    rowClass(item) {
      return item && item.duplicated ? 'ad2-row-duplicated' : '';
    },
    hasEntries(map) {
      return map != null && Object.keys(map).length > 0;
    },
    topEntries(map, limit) {
      return Object.entries(map || {})
          .map(([key, count]) => ({ key, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, limit);
    },
    countriesTooltip(map) {
      return Object.entries(map || {})
          .sort((a, b) => b[1] - a[1])
          .map(([code, count]) => `${this.countryNames[code] || code}: ${count}`)
          .join(', ');
    },
    statusesTooltip(map) {
      return Object.entries(map || {})
          .sort((a, b) => b[1] - a[1])
          .map(([id, count]) => `${this.statusName(id)}: ${count}`)
          .join(', ');
    },
    statusName(id) {
      return this.statusById[id] ? this.statusById[id].name : id;
    },
    statusColor(id) {
      return this.statusById[id] && this.statusById[id].color ? this.statusById[id].color : '#8a91ad';
    },
    formatPercent(value) {
      if (value == null) return '—';
      return `${Number(value).toFixed(2)}%`;
    },
    /**
     * Plain-words CRG standing: warn = open weeks still short of the minimum,
     * good = paid extra above it, bad = a window closed BELOW the minimum
     * (guarantee paid for missing FTDs), ok = exactly on the minimum.
     */
    crgState(row) {
      const inWindow = row.crgInWindowFtds || 0;
      const guaranteed = row.crgGuaranteedFtds || 0;
      if ((row.crgToMinimum || 0) > 0) {
        return { tone: 'warn', label: this.$t('affiliateDashboard.crgNeedsMore', { count: row.crgToMinimum }) };
      }
      if ((row.crgOverMinimum || 0) > 0) {
        return { tone: 'good', label: this.$t('affiliateDashboard.crgExtraPaid', { count: row.crgOverMinimum }) };
      }
      if (inWindow < guaranteed) {
        return { tone: 'bad', label: this.$t('affiliateDashboard.crgClosedShort', { count: guaranteed - inWindow }) };
      }
      return { tone: 'ok', label: this.$t('affiliateDashboard.crgMet') };
    },
    // Plain-language explanation of the guaranteed minimum for the cell tooltip.
    crgMinTooltip(row) {
      return this.$t('affiliateDashboard.crgMinTooltip', {
        count: row.expectedFtds || 0,
        crg: row.crg != null ? row.crg : '—',
        leads: row.leads || 0,
      });
    },
    // CRG drill: the full calculation behind the CRG columns of one affiliate.
    openCrgDetails(row) {
      if (!row || !row.affiliateId) return;
      this.$refs.crgModal.show({
        affiliateId: row.affiliateId,
        label: row.label,
        filter: { ...this.filter, range: { from: this.dateRange.from, until: this.dateRange.until } },
      });
    },
    // Full weekly-settlement breakdown behind the compact CRG-status cell.
    crgStatusTooltip(row) {
      return this.$t('affiliateDashboard.crgStatusTooltip', {
        inWindow: row.crgInWindowFtds || 0,
        guaranteed: row.crgGuaranteedFtds || 0,
        paid: row.crgPaidFtds || 0,
        freeLate: row.crgFreeLateFtds || 0,
        toGo: row.crgToMinimum || 0,
        over: row.crgOverMinimum || 0,
      });
    },
    conversionVariant(value) {
      if (value == null) return 'light-secondary';
      if (value >= 10) return 'light-success';
      if (value >= 5) return 'light-warning';
      return 'light-danger';
    },
    sortItemCompare(row1, row2, field) {
      try {
        let value1 = row1[field];
        let value2 = row2[field];
        if (field === 'countries') {
          value1 = Object.values(row1.countries || {}).reduce((a, b) => a + b, 0);
          value2 = Object.values(row2.countries || {}).reduce((a, b) => a + b, 0);
        }
        if (value1 == null) value1 = typeof value2 === 'number' ? -Infinity : '';
        if (value2 == null) value2 = typeof value1 === 'number' ? -Infinity : '';
        if (typeof value1 === 'number' && typeof value2 === 'number') {
          return value1 - value2;
        }
        if (value1.localeCompare === undefined || value2.localeCompare === undefined) {
          return 0;
        }
        return value1.localeCompare(value2);
      } catch (e) {
        console.warn('error in sortItemCompare', e);
        return 0;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ad2-info {
  margin-left: 0.3rem;
  font-size: 0.85em;
  opacity: 0.55;
  cursor: help;

  &:hover {
    opacity: 1;
  }
}

.affiliate-dashboard {
  position: relative;

  &__toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}

/* Clickable CRG cells: minimal — dotted underline on the number, soft hover */
.ad-crg-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: none;
  background: transparent;
  padding: 0.2rem 0.4rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover { background: rgba(0, 123, 255, 0.08); }
  &:focus { outline: none; }
}

/* Guaranteed minimum: one clear number */
.ad-crg-min {
  font-size: 0.95rem;
  font-weight: 800;
  color: #6d28d9;
  border-bottom: 1px dotted rgba(109, 40, 217, 0.55);
  font-variant-numeric: tabular-nums;
}

.ad-crg-count {
  font-size: 0.95rem;
  font-weight: 800;
  color: #2f2b50;
  font-variant-numeric: tabular-nums;
  border-bottom: 1px dotted rgba(0, 123, 255, 0.45);

  &__sep { opacity: 0.45; font-weight: 500; margin: 0 1px; }
}

/* State pill — plain words, one semantic color per state */
.ad-crg-state {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;

  &--warn { background: rgba(245, 158, 11, 0.16); color: #b45309; }
  &--good { background: rgba(40, 199, 111, 0.16); color: #1f9d61; }
  &--bad  { background: rgba(234, 84, 85, 0.14); color: #c0392b; }
  &--ok   { background: rgba(0, 123, 255, 0.12); color: #1d6fd8; }
}

/* Dark mode: brighter text, same translucent chips */
.dark-layout {
  .ad-crg-link:hover { background: rgba(77, 163, 255, 0.12); }
  .ad-crg-min { color: #b0a6ff; border-bottom-color: rgba(176, 166, 255, 0.55); }
  .ad-crg-count { color: #e6e8ee; border-bottom-color: rgba(124, 184, 255, 0.5); }
  .ad-crg-state {
    &--warn { background: rgba(245, 158, 11, 0.22); color: #fbbf24; }
    &--good { background: rgba(40, 199, 111, 0.22); color: #4ade80; }
    &--bad  { background: rgba(234, 84, 85, 0.22); color: #f87171; }
    &--ok   { background: rgba(77, 163, 255, 0.2); color: #7cb8ff; }
  }
}

.kpi-row {
  margin-top: 0.5rem;

  // All five KPI cards on ONE row on desktop (20% each); below xl they keep
  // the regular 2-per-row (md) / 1-per-row (sm) flow.
  @media (min-width: 1200px) {
    .kpi-col {
      flex: 0 0 20%;
      max-width: 20%;
    }
  }
}

.kpi-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.35rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  box-shadow: 0 12px 28px -14px rgba(20, 24, 50, 0.28),
              0 2px 4px rgba(20, 24, 50, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  min-height: 118px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 40px -16px rgba(20, 24, 50, 0.35),
                0 4px 8px rgba(20, 24, 50, 0.08);
  }

  &__icon {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.22);
    backdrop-filter: blur(6px);
    flex-shrink: 0;
  }

  &__body {
    position: relative;
    z-index: 2;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.82rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.86;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value {
    font-size: 1.55rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value-sep {
    opacity: 0.55;
    font-weight: 500;
    margin: 0 0.15rem;
  }

  &__footer {
    margin-top: 0.45rem;
    font-size: 0.76rem;
    opacity: 0.9;
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__glow {
    position: absolute;
    top: -40%;
    right: -20%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%);
    pointer-events: none;
    z-index: 1;
  }

  &--primary {
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%);
  }

  &--success {
    background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%);
  }

  &--warning {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%);
  }

  &--danger {
    background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%);
  }

  &--pending {
    background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 55%, #6d28d9 100%);
  }
}

.panel-card {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 10px 24px -16px rgba(20, 24, 50, 0.22),
              0 1px 3px rgba(20, 24, 50, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid rgba(110, 117, 159, 0.12);
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  &__icon-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(0, 123, 255, 0.14), rgba(0, 123, 255, 0.04));
    color: #007BFF;
  }

  &__title {
    font-size: 0.98rem;
    font-weight: 600;
    color: #1f2a44;
  }

  &__body {
    padding: 0.75rem 1rem 1rem;
    flex: 1;
  }
}

.ad2-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.ad2-groupby {
  display: inline-flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.ad2-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: rgba(110, 117, 159, 0.1);
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
  margin-right: 0.3rem;
  font-size: 0.8rem;

  &__dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
}

.ad2-foot {
  font-size: 14px;
  font-weight: bold;
}

::v-deep .ad2-row-duplicated td {
  background: rgba(245, 158, 11, 0.12) !important;
}

.affiliate-dashboard--dark {
  .panel-card {
    background: #283046;
    box-shadow: 0 10px 24px -16px rgba(0, 0, 0, 0.6);
  }

  .panel-card__header {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .panel-card__title {
    color: #d0d2d6;
  }

  .panel-card__icon-chip {
    background: linear-gradient(135deg, rgba(77, 163, 255, 0.25), rgba(77, 163, 255, 0.05));
    color: #a8b6ff;
  }

  .ad2-chip {
    background: rgba(255, 255, 255, 0.08);
  }

  ::v-deep .ad2-row-duplicated td {
    background: rgba(245, 158, 11, 0.18) !important;
  }
}

@media (max-width: 576px) {
  .kpi-card {
    min-height: auto;
    padding: 1rem;
    &__value { font-size: 1.3rem; }
  }
}
</style>
